import { Platform } from 'react-native'
import * as Linking from 'expo-linking'

import { getItem, setItem } from '../../utils/storage'
import {
  awaitInitialBranchOpen,
  initBranch,
  subscribeToBranchLinks,
} from './branch'
import { buildAttributionProperties } from './event-builders'
import { MixpanelEvents } from './event-names'
import {
  registerSuperProperties,
  setSessionProperties,
  trackEvent,
} from './mixpanel'

const ATTRIBUTION_SENT_KEY = 'install_attributed_sent'

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const

export function extractUtmParams(url: string): Record<string, string> | null {
  try {
    const parsed = Linking.parse(url)
    const params = parsed.queryParams
    if (params == null) return null

    const utmParams: Record<string, string> = {}
    let found = false
    for (const key of UTM_KEYS) {
      const value = params[key]
      if (typeof value === 'string' && value.length > 0) {
        utmParams[key] = value
        found = true
      }
    }
    return found ? utmParams : null
  } catch {
    return null
  }
}

function pickBranchUtmParams(
  params: Record<string, unknown> | null,
): Record<string, string> {
  if (params == null || params['+clicked_branch_link'] !== true) {
    return {}
  }

  const entries: [string, string][] = []
  if (typeof params['~channel'] === 'string' && params['~channel'].length > 0) {
    entries.push(['utm_source', params['~channel']])
  }
  if (typeof params['~feature'] === 'string' && params['~feature'].length > 0) {
    entries.push(['utm_medium', params['~feature']])
  }
  if (
    typeof params['~campaign'] === 'string' &&
    params['~campaign'].length > 0
  ) {
    entries.push(['utm_campaign', params['~campaign']])
  }

  return Object.fromEntries(entries)
}

function mergeAttributionParams(
  branchParams: Record<string, unknown> | null,
  urlParams: Record<string, string> | null,
): Record<string, string> | undefined {
  const merged = {
    ...(urlParams ?? {}),
    ...pickBranchUtmParams(branchParams),
  }

  return Object.keys(merged).length > 0 ? merged : undefined
}

export async function trackAttributionOnce(params: {
  platform: string
  utmParams?: Record<string, string>
}): Promise<void> {
  try {
    const alreadySent = await getItem(ATTRIBUTION_SENT_KEY)
    if (alreadySent === 'true') return

    const storeType =
      Platform.OS === 'ios'
        ? 'app_store'
        : Platform.OS === 'android'
          ? 'play_store'
          : 'web'
    const readableStore =
      Platform.OS === 'ios'
        ? 'Apple App Store'
        : Platform.OS === 'android'
          ? 'Google Play Store'
          : 'Web'

    trackEvent(
      MixpanelEvents.AppInstalled,
      buildAttributionProperties({
        platform: params.platform,
        attributionSource: params.utmParams ? 'deep_link' : 'organic',
        storeType,
        readableStore,
        utmParams: params.utmParams,
      }),
    )

    registerSuperProperties({ install_source: storeType })
    await setItem(ATTRIBUTION_SENT_KEY, 'true')
  } catch {
    // Attribution is best-effort; do not crash on failure
  }
}

/**
 * Initialise attribution tracking.
 * Sets up Branch deep-link subscription (native) and falls back to UTM
 * parameter extraction from the initial URL.
 */
export async function initAttribution(): Promise<void> {
  // UTM fallback from initial URL
  let url: string | null = null
  try {
    url = await Linking.getInitialURL()
  } catch {
    // No initial URL available
  }

  const urlUtmParams = url ? extractUtmParams(url) : null
  if (urlUtmParams != null) {
    setSessionProperties(urlUtmParams)
  }

  if (Platform.OS === 'web') {
    await trackAttributionOnce({
      platform: Platform.OS,
      utmParams: urlUtmParams ?? undefined,
    })
    return
  }

  let branchResult: {
    params: Record<string, unknown> | null
    clickedBranchLink: boolean
  } = { params: null, clickedBranchLink: false }
  try {
    await initBranch()
    branchResult = await awaitInitialBranchOpen()
    subscribeToBranchLinks()
  } catch (e) {
    console.warn('Branch init failed, falling back to UTM-only attribution', e)
  }

  const attributionParams = mergeAttributionParams(
    branchResult.params,
    urlUtmParams,
  )
  if (attributionParams != null) {
    setSessionProperties(attributionParams)
  }

  await trackAttributionOnce({
    platform: Platform.OS,
    utmParams: attributionParams,
  })
}
